import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, Mail, Shield, LogOut } from "lucide-react";
import { LOGO_URL } from "../data/firmContent";
import { useAuth } from "../context/AuthContext";

const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/practice-areas", label: "Practice Areas" },
    { to: "/publications", label: "Publications" },
    { to: "/gallery", label: "Gallery" },
    { to: "/blog", label: "Insights" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <header
            className="sticky top-0 z-50 bg-[#faf9f6]/90 backdrop-blur-xl border-b border-slate-900/10"
            data-testid="site-navbar"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
                        <img
                            src={LOGO_URL}
                            alt="Rotimi Jacobs & Co"
                            className="h-14 w-14 object-contain"
                        />
                        <div className="hidden sm:block leading-tight">
                            <div className="font-serif-display text-xl text-slate-900 font-semibold">
                                Rotimi Jacobs &amp; Co
                            </div>
                            <div className="font-mono-cap text-[10px] text-[#c5a059] mt-0.5">
                                Legal Practitioners · Est. 1997
                            </div>
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={({ isActive }) =>
                                    `rj-link text-[15px] font-medium tracking-[0.01em] ${
                                        isActive ? "text-[#c5a059]" : "text-slate-900"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        {user ? (
                            <>
                                {user.role === "admin" && (
                                    <Link
                                        to="/admin"
                                        className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#c5a059] transition-colors"
                                        data-testid="nav-admin-link"
                                    >
                                        <Shield className="w-4 h-4" />
                                        Admin
                                    </Link>
                                )}
                                <Link
                                    to="/webmail"
                                    className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#c5a059] transition-colors"
                                    data-testid="nav-webmail-link"
                                >
                                    <Mail className="w-4 h-4" />
                                    Webmail
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#c5a059] transition-colors"
                                    data-testid="nav-logout-button"
                                >
                                    <LogOut className="w-4 h-4" />
                                    Sign out
                                </button>
                            </>
                        ) : (
                            <Link
                                to="/login"
                                className="hidden md:inline-block text-sm font-medium text-slate-800 hover:text-[#c5a059] transition-colors"
                                data-testid="nav-login-link"
                            >
                                Sign in
                            </Link>
                        )}
                        <Link
                            to="/contact"
                            className="hidden md:inline-flex items-center gap-2 bg-[#0F172A] text-[#faf9f6] text-sm font-medium px-5 py-2.5 rounded-sm hover:bg-[#c5a059] hover:text-[#0F172A] transition-colors"
                            data-testid="nav-book-consultation-button"
                        >
                            Book Consultation
                        </Link>
                        <button
                            className="lg:hidden p-2 -mr-2"
                            onClick={() => setOpen(!open)}
                            data-testid="nav-mobile-toggle"
                            aria-label="Toggle menu"
                        >
                            {open ? <X /> : <Menu />}
                        </button>
                    </div>
                </div>
            </div>
            {open && (
                <div className="lg:hidden border-t border-slate-900/10 bg-[#faf9f6]" data-testid="nav-mobile-menu">
                    <div className="px-4 py-4 space-y-3">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `block py-2 text-base ${
                                        isActive ? "text-[#c5a059]" : "text-slate-800"
                                    }`
                                }
                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        {user ? (
                            <>
                                {user.role === "admin" && (
                                    <Link to="/admin" className="block py-2 text-base text-slate-800">Admin</Link>
                                )}
                                <Link to="/webmail" className="block py-2 text-base text-slate-800">Webmail</Link>
                                <button onClick={handleLogout} className="block py-2 text-base text-slate-800">Sign out</button>
                            </>
                        ) : (
                            <Link to="/login" className="block py-2 text-base text-slate-800" onClick={() => setOpen(false)}>Sign in</Link>
                        )}
                        <Link
                            to="/contact"
                            onClick={() => setOpen(false)}
                            className="block w-full text-center bg-[#0F172A] text-[#faf9f6] px-5 py-3 rounded-sm"
                        >
                            Book Consultation
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
